<template>
    <div class="acknowledge-information-corporate">
        <div class="ack-corporate-measure">
            <display-content class="ack-corporate-content" :content="activityContent.content" />

            <hr class="ack-corporate-divider" />

            <div class="ack-corporate-acknowledgment">
                <!-- Once acknowledged, the present-tense prompt reads stale, so replace
                     it with a past-tense confirmation. -->
            

                <template v-if="!isAcknowledged">
                    <h2 class="ack-corporate-heading">{{ translate('acknowledgment') }}</h2>
                    <p class="ack-corporate-statement">{{ activityContent.acknowledgment_message }}</p>
                </template>
                <template v-else>
                    <p class="ack-corporate-acknowledged">
                        <e-icon icon="check" class="is-success" />
                        {{ translate('acknowledged') }}
                    </p>
                </template>
            </div>

            <div v-if="allowStudentAttachments && !completed && !readOnly" class="ack-corporate-upload">
                <e-field label="File Upload">
                    <edverum-file-upload
                        :autosave="false"
                        :attachments="media"
                        @add="onMediaAdd"
                        @delete="onMediaDelete"
                    />
                </e-field>
            </div>

            <!-- Bottom-right in the shared action row (consistent with the other corporate
                 renderers): on completion the button hides and the shell footer's Next appears
                 in its place, right-anchored (AC-4). -->
            <activity-completion-action-row class="ack-corporate-action">
                <e-button
                    v-if="canAcknowledge"
                    :loading="isLoading"
                    :disabled="disabled"
                    type="is-primary"
                    @click="acknowledgeAndComplete"
                >
                    {{ translate('acknowledge_and_mark_complete') }}
                </e-button>
            </activity-completion-action-row>
        </div>
    </div>
</template>

<script>
import get from 'lodash/get.js';
import { getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';

import ActivityCompletionActionRow from '@/components/activity-types/course-activity-types/ActivityCompletionActionRow.vue';
import DisplayContent from '@/components/editor/DisplayContent.vue';
import EdverumFileUpload from '@/components/file-upload/EdverumFileUpload.vue';
import { useActivity } from '@/composables/useActivity.js';

/**
 * Corporate presentation of the Acknowledge Information activity.
 *
 * Behaviour is shared with the stanbridge `AcknowledgeInformation.vue` via the
 * `useActivity` composable. The corporate Figma (17111:46316) drops the explicit
 * acknowledgment CHECKBOX — the single "Acknowledge & Mark Complete" button is the
 * acknowledgment: clicking it accepts and submits. Stanbridge is unchanged; the
 * dispatcher selects this variant for the corporate client via
 * CORPORATE_RENDERER_OVERRIDES.
 */
export default {
    name: 'AcknowledgeInformationCorporate',

    components: {
        ActivityCompletionActionRow,
        DisplayContent,
        EdverumFileUpload,
    },

    props: {
        namespace: {
            type: String,
            default: null,
        },

        uuid: {
            type: String,
            default: null,
        },

        readOnly: {
            type: Boolean,
            default: false,
        },

        provideSubmissions: {
            type: Array,
            required: false,
            default: () => null,
        },
    },

    setup(props, ctx) {
        const { t: translate } = useI18n();
        const instance = getCurrentInstance();
        const api = useActivity(props, ctx, { getPayload: () => instance.proxy.payload });

        return { translate, ...api };
    },

    data: () => ({
        userAccepted: null,
        media: [],
    }),

    computed: {
        allowStudentAttachments() {
            return get(this.activity, 'activity_settings.allow_student_attachments', false);
        },

        // The acknowledge button shows while the learner can still act: not yet
        // complete (and not read-only), or holding an authorized re-attempt.
        canAcknowledge() {
            return this.userHasAuthorizedAttempt || (!this.completed && !this.readOnly);
        },

        // Completed with no pending re-attempt → swap the prompt for the confirmation.
        isAcknowledged() {
            return this.completed && !this.userHasAuthorizedAttempt;
        },

        payload() {
            return {
                uuid: this.activity.uuid,
                course_id: this.$route.params.courseId,
                activity_id: this.$route.params.activityId,
                activity_type: this.get('activity_type'),
                activity_submission: {
                    acknowledgment: this.userAccepted,
                    media: this.media,
                },
            };
        },
    },

    watch: {
        completed(completed) {
            if (completed) {
                this.userAccepted = true;
            }
        },
    },

    methods: {
        // Corporate has no checkbox: the button is the acknowledgment. Accept, then
        // submit — submit() reads `payload` (which reads userAccepted) lazily, so the
        // updated value is picked up.
        acknowledgeAndComplete() {
            this.userAccepted = true;
            this.submit();
        },

        onMediaAdd(files) {
            this.media = files;
        },

        onMediaDelete(index) {
            this.media.splice(index, 1);
        },
    },
};
</script>

<style scoped lang="scss">
.acknowledge-information-corporate {
    // The corporate shell section owns the 860px content measure; fill it.
    .ack-corporate-measure {
        max-width: 100%;
    }

    .ack-corporate-content :deep(p) {
        color: #363636; // Figma Primary/Elements/$text-strong
        font-size: 1rem; // 16px
        line-height: 1.5;
    }

    .ack-corporate-divider {
        border: none;
        border-top: 1px solid #e5e5e5;
        margin: 1.5rem 0;
    }

    .ack-corporate-heading {
        color: #363636;
        font-size: 1rem;
        font-weight: 600;
        margin-bottom: 0.5rem;
    }

    .ack-corporate-statement {
        color: #363636;
        font-size: 0.9rem;
        line-height: 1.5;
    }

    // Keep the success check icon green, but the label reads in the neutral text color.
    .ack-corporate-acknowledged {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: #363636;
        font-size: 0.9rem;
    }

    .ack-corporate-upload {
        margin-top: 1.5rem;
    }

    // Right-aligned action row (ActivityCompletionActionRow) beneath the statement; it
    // owns its own right-alignment + mobile full-width rules.
    .ack-corporate-action {
        margin-top: 1.5rem;
    }
}
</style>
